import { useState, type ReactNode } from 'react';
import { ChevronDown, Settings2, X } from 'lucide-react';
import { BIZ_STATUS_PILL, toBizStatus, bizStatusLabel } from './labels';
import { Reveal } from '../../../lib/motion';

/** A status badge that always speaks plain English. */
export function StatusPill({ raw, className }: { raw: string | null | undefined; className?: string }) {
  const s = toBizStatus(raw);
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${BIZ_STATUS_PILL[s]} ${className ?? ''}`}
    >
      {bizStatusLabel(raw)}
    </span>
  );
}

export function Pill({
  children,
  tone = 'slate',
}: {
  children: ReactNode;
  tone?: 'slate' | 'green' | 'amber' | 'red' | 'blue' | 'violet';
}) {
  const map: Record<string, string> = {
    slate: 'bg-slate-100 text-slate-600 border-slate-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-rose-50 text-rose-700 border-rose-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${map[tone]}`}>
      {children}
    </span>
  );
}

/** Page header: the question this page answers, in one line. */
export function ViewHeader({
  title,
  question,
  right,
}: {
  title: string;
  question: string;
  right?: ReactNode;
}) {
  return (
    <div className="page-head animate-fade-up">
      <div>
        <h1 className="page-title">{title}</h1>
        <p className="page-sub">{question}</p>
      </div>
      {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  );
}

/** Active-filter chips. Click the x to clear one. */
export function FilterChips({
  chips,
  onClear,
  onClearAll,
}: {
  chips: Array<{ key: string; label: string }>;
  onClear: (key: string) => void;
  onClearAll?: () => void;
}) {
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] text-slate-400">Filtered by</span>
      {chips.map((c) => (
        <button
          key={c.key}
          onClick={() => onClear(c.key)}
          className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 hover:bg-brand-100"
        >
          {c.label}
          <X className="h-3 w-3" />
        </button>
      ))}
      {chips.length > 1 && onClearAll && (
        <button onClick={onClearAll} className="text-[11px] text-slate-400 underline hover:text-slate-600">
          clear all
        </button>
      )}
    </div>
  );
}

/**
 * Everything that is true but not everyday: raw codes, sheet rows, reconciliation,
 * legends, data-quality alerts. Collapsed by default — nothing is deleted, just moved.
 */
export function OpsPanel({
  children,
  title = 'Ops details',
  hint = 'Raw codes, sheet rows, reconciliation and data-quality checks',
  defaultOpen = false,
}: {
  children: ReactNode;
  title?: string;
  hint?: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-slate-50"
      >
        <Settings2 className="h-3.5 w-3.5 text-slate-400" />
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        <span className="hidden text-[11px] text-slate-400 sm:inline">{hint}</span>
        <ChevronDown className={`ml-auto h-4 w-4 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="space-y-4 border-t border-slate-200 p-3">{children}</div>}
    </div>
  );
}

/** Key/value block used inside row-expands. */
export function Facts({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((f, i) => (
        <div key={i} className="flex items-baseline justify-between gap-3 border-b border-dashed border-slate-200 pb-1">
          <dt className="text-[11px] text-slate-500">{f.label}</dt>
          <dd className="text-right text-[12px] font-medium text-slate-800">{f.value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A titled block of content. The unit of structure on every IMS page — content
 * goes inside, and the header says what it is. Reveals on scroll.
 */
export function Section({
  title,
  hint,
  right,
  children,
  flush,
  delay,
  className,
}: {
  title: string;
  hint?: string;
  right?: ReactNode;
  children: ReactNode;
  /** Tables sit flush — no inner padding. */
  flush?: boolean;
  delay?: number;
  /** Extra classes on the section frame — use `accent-blue|emerald|amber|rose|
   *  violet|cyan|teal|indigo` to colour the head's left rule so related blocks
   *  are grouped by hue. */
  className?: string;
}) {
  return (
    <Reveal delay={delay}>
      <section className={`section ${className ?? ''}`}>
        <div className="section-head">
          <h2 className="section-title">{title}</h2>
          {hint && <span className="section-hint">{hint}</span>}
          {right && <div className="flex flex-wrap items-center gap-2 sm:ml-auto">{right}</div>}
        </div>
        <div className={flush ? 'section-flush' : 'section-body'}>{children}</div>
      </section>
    </Reveal>
  );
}

export { Reveal };
